/**
 * Ticket Detail Page
 * Displays detailed information about a ticket with comments and actions
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material'
import { ArrowBack, Close } from '@mui/icons-material'
import { getTicketDetails, addComment, closeTicket } from '@api/services/tickets.api'
import { TicketDetailsDto, AddCommentRequest, TicketStatus, isValidationTicketType } from '@api/types/tickets'
import { TicketEscalationChip } from '../components/TicketEscalationChip'
import { TicketStatusBadge } from '../components/TicketStatusBadge'
import { TicketPriorityIndicator } from '../components/TicketPriorityIndicator'
import { TicketTypeIcon } from '../components/TicketTypeIcon'
import { TicketCommentThread } from '../components/TicketCommentThread'
import { TicketPhotoGrid } from '../components/TicketPhotos'
import { TicketTargetLink } from '../components/TicketTargetLink'
import { TicketValidationChips } from '../components/TicketValidationChips'
import { useAuthStore } from '@store/authStore'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { PageShell } from '@components/common/PageShell'
import { ConfirmDialog } from '@components/common/ConfirmDialog'

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [ticket, setTicket] = useState<TicketDetailsDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  /** A failed action: shown above the ticket, never in place of it (`error` is for a failed load). */
  const [actionError, setActionError] = useState<string | null>(null)

  /** `quiet`: refresh in place after a comment, without the full-page spinner or losing the scroll. */
  const loadTicket = async (quiet = false) => {
    if (!ticketId) return

    if (!quiet) setLoading(true)
    setError(null)
    try {
      const data = await getTicketDetails(ticketId)
      setTicket(data)
    } catch (err: any) {
      console.error('Failed to load ticket:', err)
      setError(err.message || 'Failed to load ticket')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTicket()
  }, [ticketId])

  const handleAddComment = async (request: AddCommentRequest) => {
    if (!ticketId) return

    setIsSubmitting(true)
    try {
      await addComment(ticketId, request)
      // Reload ticket to get updated comments
      await loadTicket(true)
    } catch (error) {
      console.error('Failed to add comment:', error)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseTicket = async () => {
    if (!ticketId || !ticket) return

    setIsSubmitting(true)
    setActionError(null)
    try {
      const updatedTicket = await closeTicket(ticketId)
      setTicket(updatedTicket)
      setConfirmClose(false)
    } catch (err: any) {
      console.error('Failed to close ticket:', err)
      setActionError(err.message || 'Failed to close ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate('/support/tickets')
  }

  if (loading) {
    return (
      <PageShell title="Ticket">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    )
  }

  if (error || !ticket) {
    return (
      <PageShell title="Ticket">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Ticket not found'}
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Tickets
        </Button>
      </PageShell>
    )
  }

  const isCreator = user?.userId === ticket.createdByUserId
  const isModerator = user?.roles?.includes('Moderator') || user?.roles?.includes('Admin')
  const canClose = isCreator && ticket.status !== TicketStatus.Closed && ticket.status !== TicketStatus.Resolved

  return (
    <PageShell title={`Ticket #${ticket.id.slice(0, 8)}`}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button variant="text" startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Tickets
        </Button>

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <TicketTypeIcon type={ticket.type} size="large" />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" gutterBottom>
                {ticket.title}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <TicketStatusBadge status={ticket.status} />
                <TicketPriorityIndicator priority={ticket.priority} />
                {ticket.isEscalated && <Chip label="ESCALATED" color="error" size="small" />}
                <TicketEscalationChip level={ticket.escalationLevel} label={ticket.escalationLabel} paused={ticket.escalationPaused} />
                <TicketValidationChips type={ticket.type} itemKey={ticket.itemKey} />
              </Box>

              <Typography variant="body2" color="text.secondary">
                Created {safeFormatDistanceToNow(ticket.createdAt)} by{' '}
                <strong>{ticket.createdByDisplayName || 'Unknown'}</strong>
              </Typography>
            </Box>

            {canClose && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Close />}
                onClick={() => setConfirmClose(true)}
                disabled={isSubmitting}
              >
                Close Ticket
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {ticket.description}
            </Typography>
            <TicketPhotoGrid attachments={ticket.attachments} />
          </Box>

          {/* Tags */}
          {ticket.tags && ticket.tags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {ticket.tags.map((tag: string | { id: string; displayName?: string; name?: string }) => {
                  const tagId = typeof tag === 'string' ? tag : tag.id
                  const tagLabel = typeof tag === 'string' ? tag : (tag.displayName || tag.name)
                  return (
                    <Chip key={tagId} label={tagLabel} size="small" />
                  )
                })}
              </Box>
            </Box>
          )}

          {/* Reported Content Link */}
          {(ticket.targetType !== null && ticket.targetId !== null) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {isValidationTicketType(ticket.type) ? 'Adventure' : 'Reported Content'}
              </Typography>
              <TicketTargetLink
                targetType={ticket.targetType}
                targetId={ticket.targetId}
                variant="chip"
              />
            </Box>
          )}

          {/* Metadata Grid */}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Ticket ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {ticket.id.slice(0, 8)}...
              </Typography>
            </Grid>

            {ticket.claimedByDisplayName && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Claimed By
                </Typography>
                <Typography variant="body2">{ticket.claimedByDisplayName}</Typography>
              </Grid>
            )}

            {ticket.assignedToDisplayName && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Assigned To
                </Typography>
                <Typography variant="body2">{ticket.assignedToDisplayName}</Typography>
              </Grid>
            )}

            {ticket.resolvedByDisplayName && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Resolved By
                </Typography>
                <Typography variant="body2">{ticket.resolvedByDisplayName}</Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {safeFormatDistanceToNow(ticket.lastUpdatedAt)}
              </Typography>
            </Grid>
          </Grid>

          {/* Resolution Notes */}
          {ticket.resolutionNotes && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="success">
                <Typography variant="subtitle2" gutterBottom>
                  Resolution Notes
                </Typography>
                <Typography variant="body2">{ticket.resolutionNotes}</Typography>
              </Alert>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Comments Section */}
      <TicketCommentThread
        comments={ticket.comments}
        onAddComment={handleAddComment}
        canAddInternalComments={isModerator}
        isSubmitting={isSubmitting}
      />
      <ConfirmDialog
        open={confirmClose}
        title="Close this ticket?"
        message="It can be reopened if needed."
        confirmLabel="Close ticket"
        loading={isSubmitting}
        onConfirm={handleCloseTicket}
        onClose={() => setConfirmClose(false)}
      />
    </PageShell>
  )
}
